import {
  MasonryEngineActionHandler,
  MasonryEngineNodeAction,
} from '@leagueplatform/masonry-engine';
import { MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import { toastActions, TOAST_STATUS } from '@leagueplatform/toast-messages';

type BenefitsPrintCardAction = MasonryEngineNodeAction<
  'printCard',
  {
    printSelectorId: string;
  }
>;

const logError = (message: string) => {
  // eslint-disable-next-line no-console
  console.error(message);

  toastActions.add({
    type: TOAST_STATUS.ERROR,
    textId: 'SOMETHING_WENT_WRONG_HEADING',
  });
};

type BenefitsPrintCardActionHandler = MasonryEngineActionHandler<
  BenefitsPrintCardAction,
  MasonryDriverAction
>;

export const printCardActionHandler: BenefitsPrintCardActionHandler = async (
  action,
) => {
  const { payload } = action;
  const { printSelectorId } = payload;

  if (!printSelectorId) {
    logError(
      'BenefitsPrintCardActionHandler: Missing required selector parameter',
    );
    return;
  }

  const querySelector = `[data-print-card="${printSelectorId}"]`;
  try {
    // Find all elements matching the selector
    const elements = document.querySelectorAll(querySelector);

    if (elements.length === 0) {
      logError(
        'BenefitsPrintCardActionHandler: No elements found matching selector',
      );
      return;
    }

    // Create a temporary container for print content
    const printContainer = document.createElement('div');
    printContainer.id = 'print-card-container';
    printContainer.style.display = 'none';

    // Copy each matching element to the print container
    elements.forEach((element) => {
      const clonedElement = element.cloneNode(true) as HTMLElement;
      clonedElement.className = `${clonedElement.className} print-card`;
      printContainer.appendChild(clonedElement);
    });

    // Save current scroll position
    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;

    // Add print styles
    const printStyleElement = document.createElement('style');
    printStyleElement.id = 'print-card-styles';
    printStyleElement.textContent = `
      @media print {
        body > *:not(#print-card-container) {
          display: none !important;
        }

        #print-card-container {
          display: block !important;
          position: static;
          top: ${currentScrollY}px;
          left: ${currentScrollX}px;
        }

        .print-card {
          max-width: 3.375in;
          break-inside: avoid;
        }

        .print-card * {
          max-width: 100%;
        }

        @page {
          margin: 1in;
        }
      }
    `;

    // Add elements to document
    document.head.appendChild(printStyleElement);
    document.body.appendChild(printContainer);

    // Print the page
    window.print();

    // Clean up after printing
    setTimeout(() => {
      document.head.removeChild(printStyleElement);
      document.body.removeChild(printContainer);
    }, 100);
  } catch (error) {
    logError(
      `BenefitsPrintCardActionHandler: Failed to print elements - ${error}`,
    );
  }
};
